import React, { type ReactNode } from 'react';
import { useFeatureGate, useFeatureFlagsReady } from './useFeatureGate';
import type { FeatureGateName } from './gates';

export interface FeatureGatedProps {
  /** Typed Statsig gate name from the `FeatureGate` registry. */
  gate: FeatureGateName;
  /** Rendered when the gate evaluates to `true`. */
  children: ReactNode;
  /**
   * Rendered when the gate has resolved to `false`. Defaults to `null` so
   * omitting it simply hides the gated content. Pass a `<Navigate>` to send
   * users elsewhere when the gate is OFF (typical for gated routes).
   */
  fallback?: ReactNode;
  /**
   * Rendered while the gate is still loading, before it has resolved either
   * way. Defaults to `null`. Route-level gates should pass a spinner so a hard
   * refresh waits for the fetch instead of rendering `fallback` (e.g.
   * redirecting home) on a not-yet-loaded value.
   */
  pending?: ReactNode;
}

/**
 * Declarative wrapper that renders `children` only when the supplied
 * Statsig gate is ON. Use it at the route level to gate whole pages, or
 * inline to gate a button / panel / row.
 *
 * Why a component (instead of just calling `useFeatureGate` everywhere):
 * the wrapper keeps gate decisions co-located with the JSX they affect,
 * reads more naturally in route trees (`<Route element={<FeatureGated …>
 * <Page />}>`), and makes it grep-able to find every gated surface.
 */
const FeatureGated: React.FC<FeatureGatedProps> = ({
  gate,
  children,
  fallback = null,
  pending = null,
}) => {
  const ready = useFeatureFlagsReady();
  const isEnabled = useFeatureGate(gate);
  if (!ready) return <>{pending}</>;
  return <>{isEnabled ? children : fallback}</>;
};

export default FeatureGated;
